import { Button } from '@cloudflare/kumo';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface AiStatus { available: boolean; jev?: boolean; model: string | null; quota: { used: number; limit: number } | null }

// Status compartilhado entre o botão do topo e as ações do editor (uma consulta por carga da página)
let statusPromise: Promise<AiStatus | null> | null = null;
export function useAiStatus() {
  const fetchApi = useAuthenticatedFetch();
  const [status, setStatus] = useState<AiStatus | null>(null);
  useEffect(() => {
    statusPromise ??= fetchApi('/api/ai/status').then((r) => (r.ok ? r.json() : null)).catch(() => null);
    let alive = true;
    statusPromise.then((s) => { if (alive) setStatus(s); });
    return () => { alive = false; };
  }, [fetchApi]);
  return status;
}

const PAGE_CONTEXT: Array<[RegExp, string]> = [
  [/^\/campanhas\/interativa/, 'O usuário está nas campanhas interativas (fluxos de conversa).'],
  [/^\/contatos|^\/segmentos/, 'O usuário está gerenciando contatos e segmentos.'],
  [/^\/whatsapp/, 'O usuário está nas conexões oficiais (WhatsApp Cloud API da Meta e Instagram).'],
  [/^\/ferramentas/, 'O usuário está nas ferramentas (API oficial, Instagram, cobranças Pix, cupons, imagens dinâmicas, Lead Ads).'],
];

const SUGGESTIONS = [
  'Escreva uma mensagem de promoção para clientes antigos',
  'Como faço meu template ser aprovado pela Meta?',
  'Me explique a diferença entre entregue e lida',
  'Dê 3 ideias de campanha para aumentar as vendas',
];

interface Msg { role: 'user' | 'assistant'; content: string }

/** Botão "Assistente" no topo + painel de conversa */
export function AiAssistant() {
  const status = useAiStatus();
  const fetchApi = useAuthenticatedFetch();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, busy]);
  if (!status?.available) return null;

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    const next = [...messages, { role: 'user' as const, content: q }];
    setMessages(next); setInput(''); setBusy(true);
    try {
      const context = PAGE_CONTEXT.find(([re]) => re.test(pathname))?.[1] ?? '';
      const res = await fetchApi('/api/ai/copilot', { method: 'POST', body: JSON.stringify({ messages: next, context }) });
      const body = await res.json().catch(() => ({}));
      setMessages([...next, { role: 'assistant', content: res.ok ? body.reply : `⚠️ ${body.message || 'Não foi possível responder agora.'}` }]);
    } catch {
      setMessages([...next, { role: 'assistant', content: '⚠️ Não foi possível responder agora.' }]);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); } catch { /* sem permissão */ } };

  return (
    <>
      <Button variant="ghost" shape="square" icon={ICONS.Sparkle} aria-label="Assistente de IA" onClick={() => setOpen((v) => !v)} />
      {open && (
        <aside className="ai-panel" aria-label="Assistente de IA">
          <header>
            <span className="ai-panel__icon"><ICONS.Sparkle size={16} /></span>
            <div>
              <strong>Assistente</strong>
              <small>Escreve mensagens, sugere campanhas e explica resultados</small>
            </div>
            {messages.length > 0 && <Button variant="ghost" shape="square" icon={ICONS.Refresh} aria-label="Nova conversa" onClick={() => setMessages([])} />}
            <Button variant="ghost" shape="square" icon={ICONS.X} aria-label="Fechar assistente" onClick={() => setOpen(false)} />
          </header>
          <div className="ai-panel__chat" aria-live="polite">
            {messages.length === 0 && (
              <div className="ai-panel__empty">
                <p>Como posso ajudar?</p>
                {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => send(s)}>{s}</button>)}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`ai-msg ai-msg--${m.role}`}>
                <p>{m.content}</p>
                {m.role === 'assistant' && !m.content.startsWith('⚠️') && (
                  <button type="button" className="ai-msg__copy" onClick={() => copy(m.content)}><ICONS.Copy size={16} /> Copiar</button>
                )}
              </div>
            ))}
            {busy && <div className="ai-msg ai-msg--assistant ai-msg--typing"><span /><span /><span /></div>}
            <div ref={endRef} />
          </div>
          <form className="ai-panel__form" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="Pergunte ou peça uma mensagem…"
              rows={2}
              aria-label="Mensagem para o assistente"
            />
            <Button type="submit" variant="ghost" shape="square" icon={ICONS.ArrowRight} aria-label="Enviar" disabled={!input.trim() || busy} />
          </form>
          <small className="ai-panel__foot">Respostas geradas por IA podem conter erros. Revise antes de enviar aos clientes.</small>
        </aside>
      )}
    </>
  );
}

const TEXT_ACTIONS: Array<[string, string]> = [
  ['improve', 'Melhorar o texto'],
  ['shorten', 'Encurtar'],
  ['formal', 'Tom mais formal'],
  ['casual', 'Tom mais descontraído'],
  ['variations', 'Gerar 3 variações (teste A/B)'],
];

/** Ações de IA no editor de mensagem: melhorar, encurtar, mudar o tom ou gerar variações */
export function AiTextActions({ text, onText, onVariations }: { text: string; onText: (t: string) => void; onVariations?: (v: string[]) => void }) {
  const status = useAiStatus();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  if (!status?.available) return null;

  const run = async (action: string) => {
    const source = text.trim();
    if (!source) { toast.error('Escreva a mensagem (ou o que ela deve dizer) primeiro'); return; }
    setBusy(true);
    try {
      const res = await fetchApi('/api/ai/texto', { method: 'POST', body: JSON.stringify({ action, text: source, count: 3 }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'A IA não respondeu');
      if (action === 'variations') {
        if (!body.variations?.length) throw new Error('A IA não gerou variações');
        onVariations?.(body.variations);
        toast.success(`${body.variations.length} variações criadas`);
      } else {
        onText(body.text);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'A IA não respondeu');
    } finally {
      setBusy(false);
    }
  };

  const items: Array<[string, string]> = [
    ...(!text.trim() ? [['write', 'Escrever a partir da ideia'] as [string, string]] : []),
    ...TEXT_ACTIONS.filter(([id]) => id !== 'variations' || onVariations),
    ...(text.trim() ? [['write', 'Reescrever do zero a partir do texto'] as [string, string]] : []),
  ];
  return (
    <div className="ai-actions" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <Button variant="secondary" icon={ICONS.Sparkle} loading={busy} disabled={busy} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Escrever com IA
      </Button>
      {open && (
        <div className="ai-actions__menu" role="menu">
          {items.map(([id, label]) => (
            <button key={id + label} type="button" role="menuitem" onClick={() => { setOpen(false); run(id); }}>{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
